import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import {
  CollectionReference,
  DocumentData,
  Firestore,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  startAfter,
  where
} from '@angular/fire/firestore';

export interface LoggingRecord {
  id: string;
  email: string;
  schoolId: string;
  path: string;
  time: Timestamp | null;
  userId: string;
}

export interface LoggingRecordDoc extends DocumentData {
  email?: string;
  schoolId?: string;
  path?: string;
  time?: Timestamp;
  id?: string;
}

export interface LoggingPage {
  records: LoggingRecord[];
  firstVisibleDoc?: QueryDocumentSnapshot<LoggingRecordDoc>;
  lastVisibleDoc?: QueryDocumentSnapshot<LoggingRecordDoc>;
  hasNextPage: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly locale = inject(LOCALE_ID);
  private readonly firestore = inject(Firestore);
  private readonly loggingCollection = collection(this.firestore, 'logging') as CollectionReference<LoggingRecordDoc>;

  async getLoggingPage(
    schoolId: string,
    pageSize: number,
    startAfterDoc?: QueryDocumentSnapshot<LoggingRecordDoc>,
    endBeforeDoc?: QueryDocumentSnapshot<LoggingRecordDoc>
  ): Promise<LoggingPage> {
    const loggingQuery = startAfterDoc
      ? query(
        this.loggingCollection,
        where('schoolId', '==', schoolId),
        orderBy('time', 'desc'),
        startAfter(startAfterDoc),
        limit(pageSize)
      )
      : endBeforeDoc
        ? query(
          this.loggingCollection,
          where('schoolId', '==', schoolId),
          orderBy('time', 'desc'),
          endBefore(endBeforeDoc),
          limitToLast(pageSize)
        )
        : query(
          this.loggingCollection,
          where('schoolId', '==', schoolId),
          orderBy('time', 'desc'),
          limit(pageSize)
        );

    const snapshot = await getDocs(loggingQuery);
    const firstVisibleDoc = snapshot.docs.length > 0 ? snapshot.docs[0] : undefined;
    const lastVisibleDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined;

    return {
      records: snapshot.docs.map(docSnapshot => this.toRecord(docSnapshot)),
      firstVisibleDoc,
      lastVisibleDoc,
      hasNextPage: snapshot.docs.length === pageSize
    };
  }

  async getRecentLogging(
    schoolId: string,
    limitCount: number
  ): Promise<{ records: LoggingRecord[]; capped: boolean }> {
    const page = await this.getLoggingPage(schoolId, limitCount);
    return {
      records: page.records,
      capped: page.hasNextPage
    };
  }

  async exportLoggingCsv(schoolId: string, startDate: Date): Promise<{ csv: string; filename: string }> {
    const batchSize = 500;
    const rows: LoggingRecord[] = [];
    let lastVisibleDoc: QueryDocumentSnapshot<LoggingRecordDoc> | undefined;

    while (true) {
      const exportQuery = lastVisibleDoc
        ? query(
          this.loggingCollection,
          where('schoolId', '==', schoolId),
          where('time', '>=', Timestamp.fromDate(startDate)),
          orderBy('time', 'desc'),
          startAfter(lastVisibleDoc),
          limit(batchSize)
        )
        : query(
          this.loggingCollection,
          where('schoolId', '==', schoolId),
          where('time', '>=', Timestamp.fromDate(startDate)),
          orderBy('time', 'desc'),
          limit(batchSize)
        );

      const snapshot = await getDocs(exportQuery);
      if (snapshot.empty) {
        break;
      }

      rows.push(...snapshot.docs.map(docSnapshot => this.toRecord(docSnapshot)));
      lastVisibleDoc = snapshot.docs[snapshot.docs.length - 1];

      if (snapshot.docs.length < batchSize) {
        break;
      }
    }

    return {
      csv: this.convertToCsv(rows),
      filename: `analytics-${schoolId}-since-2026-03-01.csv`
    };
  }

  private convertToCsv(records: LoggingRecord[]): string {
    const header = ['Time', 'Email', 'Path', 'User ID', 'School ID'];
    const rows = records.map(record => [
      this.formatTimestamp(record.time),
      record.email || 'Unknown user',
      record.path || '/',
      record.userId || 'Unknown ID',
      record.schoolId || ''
    ]);

    return [header, ...rows]
      .map(row => row.map(value => this.escapeCsvValue(value)).join(','))
      .join('\r\n');
  }

  private formatTimestamp(timestamp: Timestamp | null): string {
    if (!timestamp) {
      return '';
    }

    return formatDate(timestamp.toDate(), 'medium', this.locale);
  }

  private escapeCsvValue(value: string): string {
    const normalizedValue = `${value ?? ''}`.replace(/"/g, '""');
    return `"${normalizedValue}"`;
  }

  private toRecord(docSnapshot: QueryDocumentSnapshot<LoggingRecordDoc>): LoggingRecord {
    const data = docSnapshot.data();

    return {
      id: docSnapshot.id,
      email: data.email ?? '',
      schoolId: data.schoolId ?? '',
      path: data.path ?? '',
      time: data.time,
      userId: data.id ?? ''
    };
  }
}
